import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WifiOff, CloudOff, RefreshCw, Check } from 'lucide-react';
import { offlineSync } from '../../services/offlineSync';

export const OfflineBanner: React.FC = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncResult, setSyncResult] = useState<{ synced: number; failed: number } | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Auto-sync cuando vuelve la conexion
      handleSync();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const unsub = offlineSync.onPendingChange((count) => {
      setPendingCount(count);
    });

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      unsub();
    };
  }, []);

  // Limpiar el resultado de sync despues de 4 segundos
  useEffect(() => {
    if (syncResult) {
      const timer = setTimeout(() => setSyncResult(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [syncResult]);

  const handleSync = async () => {
    setIsSyncing(true);
    const result = await offlineSync.syncPending();
    setIsSyncing(false);
    if (result.synced > 0 || result.failed > 0) {
      setSyncResult(result);
    }
  };

  // No mostrar nada si esta online y no hay pendientes ni resultado
  const showBanner = !isOnline || pendingCount > 0 || syncResult;
  if (!showBanner) return null;

  return (
    <AnimatePresence>
      {/* Banner offline */}
      {!isOnline && (
        <motion.div
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -40 }}
          className="fixed top-0 left-0 right-0 z-[200] px-4 py-2.5 flex items-center justify-center gap-2 text-sm font-medium text-white"
          style={{ background: '#6b7280' }}
        >
          <WifiOff size={16} />
          <span>Sin conexion - Los cambios se guardaran cuando vuelvas a tener internet</span>
        </motion.div>
      )}

      {/* Banner de pendientes (online pero con operaciones sin sincronizar) */}
      {isOnline && pendingCount > 0 && !syncResult && (
        <motion.div
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -40 }}
          className="fixed top-0 left-0 right-0 z-[200] px-4 py-2.5 flex items-center justify-center gap-2 text-sm font-medium text-white"
          style={{ background: '#f59e0b' }}
        >
          <CloudOff size={16} />
          <span>{pendingCount} cambio{pendingCount > 1 ? 's' : ''} pendiente{pendingCount > 1 ? 's' : ''} de sincronizar</span>
          <button
            onClick={handleSync}
            disabled={isSyncing}
            className="ml-2 px-3 py-1 rounded-lg bg-white/20 hover:bg-white/30 transition-colors flex items-center gap-1"
          >
            <RefreshCw size={14} className={isSyncing ? 'animate-spin' : ''} />
            {isSyncing ? 'Sincronizando...' : 'Sincronizar'}
          </button>
        </motion.div>
      )}

      {/* Banner de resultado de sync */}
      {syncResult && (
        <motion.div
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -40 }}
          className="fixed top-0 left-0 right-0 z-[200] px-4 py-2.5 flex items-center justify-center gap-2 text-sm font-medium text-white"
          style={{ background: syncResult.failed > 0 ? '#ef4444' : '#22c55e' }}
        >
          <Check size={16} />
          <span>
            {syncResult.synced > 0 && `${syncResult.synced} cambio${syncResult.synced > 1 ? 's' : ''} sincronizado${syncResult.synced > 1 ? 's' : ''}`}
            {syncResult.failed > 0 && ` | ${syncResult.failed} fallido${syncResult.failed > 1 ? 's' : ''}`}
          </span>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
